import { randomUUID } from '@exodus/crypto/randomUUID'
import { bufferToHex } from '@exodus/ethereumjs/util'
import fetchival from '@exodus/fetch/experimental/fetchival'
import { retry } from '@exodus/simple-retry'
import SolidityContract from '@exodus/solidity-contract'
import ms from 'ms'

import { fromHexToString } from '../number-utils.js'
import createWebSocket from './ws.js'

const RETRY_DELAYS = ['10s']

export function create(defaultURL, ensAssetName) {
  let API_URL = defaultURL
  const ws = createWebSocket(() => {
    const obj = new URL(API_URL)
    obj.protocol = 'wss:'
    return `${obj}`
  })

  function baseUrl(version = 'v1') {
    return new URL(version === 'v1' ? API_URL : API_URL.replace('v1', version))
  }

  async function request(module, params = {}, { version = 'v1', method = 'get' } = {}) {
    try {
      return await fetchival(baseUrl(version), { timeout: ms('15s') })(module).method(
        method,
        params
      )
    } catch (err) {
      let nerr = err
      if (err.response && (err.response.status === 500 || err.response.status === 400)) {
        try {
          const data = await err.response.json()
          const msg = data.error.replace(/^RPC error \(code: -\d+\): /, '')
          nerr = new Error(msg)
        } catch {}

        nerr.finalError = true
      }

      throw nerr
    }
  }

  // Default retry function
  const requestWithRetry = retry(request, { delayTimesMs: RETRY_DELAYS })

  return {
    getURL() {
      return API_URL
    },

    setURL(newURL) {
      if (newURL === API_URL) return // prevents useless WS reconnections

      API_URL = newURL || defaultURL

      if (ws.isCreated()) {
        ws.close()
        ws.open()
      }
    },

    stop() {
      ws.close()
    },

    ws,

    async getBalance(address, opts) {
      opts = { startblock: 'earliest', endblock: 'pending', ...opts }
      return requestWithRetry('balance', { address, from: opts.startblock, to: opts.endblock })
    },

    async getBalanceProxied(address, tag = 'latest') {
      return requestWithRetry('proxy', { method: 'eth_getBalance', address, tag })
    },

    async balanceOf(address, tokenAddress, tag = 'latest') {
      const contract = SolidityContract.simpleErc20(tokenAddress)
      const callData = contract.balanceOf.build(address)
      const data = {
        data: bufferToHex(callData),
        to: tokenAddress,
        tag,
      }
      const result = await retry(this.ethCall, { delayTimesMs: RETRY_DELAYS })(data)
      const balance = fromHexToString(result)
      return {
        confirmed: {
          [tokenAddress]: balance,
        },
      }
    },

    async getHistory(address, opts) {
      opts = { startblock: 'earliest', endblock: 'pending', limit: 1000, ...opts }
      return requestWithRetry('history', {
        address,
        from: opts.startblock,
        to: opts.endblock,
        limit: opts.limit,
      })
    },

    async getHistoryV2(address, opts = {}) {
      return requestWithRetry(
        'history',
        {
          address,
          ...opts,
        },
        { version: 'v2' }
      )
    },

    async gasPrice() {
      return requestWithRetry('proxy', { method: 'eth_gasPrice' })
    },

    // for fee monitor
    async getGasPrice() {
      return requestWithRetry('proxy', { method: 'eth_gasPrice' })
    },

    async getLatestBlock() {
      return this.getBlockByNumber('latest')
    },

    async getBaseFeePerGas() {
      const response = await this.getLatestBlock()
      if (response.baseFeePerGas) {
        return fromHexToString(response.baseFeePerGas)
      }
    },

    async getTransactionCount(address, tag = 'latest') {
      return requestWithRetry('proxy', { method: 'eth_getTransactionCount', address, tag })
    },

    async getTransactionByHash(hash) {
      return requestWithRetry('proxy', { method: 'eth_getTransactionByHash', hash })
    },

    async getTransactionReceipt(txhash) {
      return requestWithRetry('proxy', { method: 'eth_getTransactionReceipt', txhash })
    },

    async getCode(address, tag = 'latest') {
      return requestWithRetry('proxy', { method: 'eth_getCode', address, tag })
    },

    async getStorageAt(address, slot, tag = 'latest') {
      return requestWithRetry('proxy', { method: 'eth_getStorageAt', address, slot, tag })
    },

    async isContract(address) {
      const code = await requestWithRetry('proxy', { method: 'eth_getCode', address })
      return code.length > 2
    },

    async sendRawTransaction(data) {
      const _data = data instanceof Uint8Array ? Buffer.from(data).toString('hex') : data
      const hex = _data.startsWith('0x') ? _data : '0x' + _data
      return requestWithRetry(
        'proxy',
        { method: 'eth_sendRawTransaction', params: [hex] },
        { version: 'v2', method: 'post' }
      )
    },

    async estimateGas(data, tag = 'latest') {
      return requestWithRetry('proxy', { method: 'eth_estimateGas', ...data, tag })
    },

    /**
     * Hits a magnifier endpoint returning maximum gas limit that a specified
     * contract along with method consumed in the past.
     * @param opts : Object with parameters
     * @returns {Promise<*>}
     */
    async getGasEstimation({ address, data }) {
      return requestWithRetry('gas-estimation', {
        address,
        data,
      })
    },

    async ethCall(data, tag = 'latest') {
      return request('proxy', { method: 'eth_call', ...data, tag })
    },

    async blockNumber() {
      return requestWithRetry('proxy', { method: 'eth_blockNumber' })
    },

    async getBlockByNumber(number, isFullTransactions = false) {
      return requestWithRetry('proxy', {
        method: 'eth_getBlockByNumber',
        number,
        isFullTransactions,
      })
    },

    async getLogs(params) {
      return requestWithRetry('proxy', { method: 'eth_getLogs', ...params })
    },

    async simulateRawTransaction(rawTransaction, applyPending = true) {
      rawTransaction = rawTransaction.replace('0x', '')
      return request('proxy', {
        method: 'debug_simulateRawTransaction',
        rawTransaction,
        applyPending,
      })
    },

    async getCoinbase() {
      return request('proxy', {
        method: 'eth_coinbase',
      })
    },

    async getBlockByHash(blockHash, fullTx = false) {
      return request('proxy', {
        method: 'eth_getBlockByHash',
        blockHash,
        fullTx,
      })
    },

    async getBlockTransactionCountByHash(blockHash) {
      return request('proxy', {
        method: 'eth_getBlockTransactionCountByHash',
        blockHash,
      })
    },

    async getBlockTransactionCountByNumber(quantityOrTag) {
      return request('proxy', {
        method: 'eth_getBlockTransactionCountByNumber',
        quantityOrTag,
      })
    },

    async getCompilers() {
      return request('proxy', {
        method: 'eth_getCompilers',
      })
    },

    async getNetVersion() {
      return request('proxy', {
        method: 'net_version',
      })
    },

    async proxyToCoinNode(requestData) {
      // eslint-disable-next-line @exodus/mutable/no-param-reassign-prop-only
      if (!requestData.jsonrpc) requestData.jsonrpc = '2.0'
      // eslint-disable-next-line @exodus/mutable/no-param-reassign-prop-only
      if (!requestData.id) requestData.id = randomUUID()
      // eslint-disable-next-line @exodus/mutable/no-param-reassign-prop-only
      if (!requestData.params) requestData.params = []

      return request('proxy', requestData, { version: 'v2', method: 'post' })
    },
  }
}
